import {
  collection,
  addDoc,
  updateDoc,
  deleteDoc,
  doc,
  getDocs,
  query,
  where,
  orderBy,
  serverTimestamp,
  Timestamp,
  QuerySnapshot,
  DocumentData
} from 'firebase/firestore';
import { db } from '../firebase/config';
import { EncryptionService } from './encryptionService';

const THOUGHTS_COLLECTION = 'thoughts';

export interface Thought {
  id: string;
  userId: string;
  title: string;
  content: string;
  labels: string[];
  createdAt: Timestamp;
  updatedAt: Timestamp;
}

export interface ThoughtInput {
  title?: string;
  content?: string;
  labels?: string[];
}

export interface ThoughtUpdate {
  title?: string;
  content?: string;
  labels?: string[];
}

// Create a new thought
export const createThought = async (
  userId: string, 
  thoughtData: ThoughtInput,
  passkey: string
): Promise<string> => {
  try {
    console.log('🔐 Creating thought with passkey of length:', passkey.length);
    console.log('   Title:', thoughtData.title);
    
    // Encrypt title and content before storing
    const encryptedTitle = EncryptionService.encrypt(
      thoughtData.title || 'Untitled Thought',
      passkey
    );
    const encryptedContent = EncryptionService.encrypt(
      thoughtData.content || '',
      passkey
    );

    console.log('   Encrypted title length:', encryptedTitle.length);

    const thoughtRef = await addDoc(collection(db, THOUGHTS_COLLECTION), {
      userId,
      title: encryptedTitle,
      content: encryptedContent,
      labels: thoughtData.labels || [],
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp()
    });
    
    console.log('✅ Thought created with ID:', thoughtRef.id);
    return thoughtRef.id;
  } catch (error) {
    console.error('Error creating thought:', error);
    throw error;
  }
};

// Get all thoughts for a user
export const getUserThoughts = async (
  userId: string, 
  passkey: string,
  labelFilter: string | null = null
): Promise<Thought[]> => {
  try {
    let q;
    
    console.log('📊 getUserThoughts called with labelFilter:', labelFilter);
    
    if (labelFilter && labelFilter !== 'all') {
      // Filter by label
      console.log('🔍 Filtering by label:', labelFilter);
      q = query(
        collection(db, THOUGHTS_COLLECTION),
        where('userId', '==', userId),
        where('labels', 'array-contains', labelFilter),
        orderBy('updatedAt', 'desc')
      );
    } else {
      // Get all thoughts
      console.log('🔍 Getting all thoughts');
      q = query(
        collection(db, THOUGHTS_COLLECTION),
        where('userId', '==', userId),
        orderBy('updatedAt', 'desc')
      );
    }

    const querySnapshot = await getDocs(q);
    console.log(`📦 Query returned ${querySnapshot.size} documents (from ${querySnapshot.metadata.fromCache ? 'cache' : 'server'})`);
    
    const thoughts: Thought[] = [];
    querySnapshot.forEach((docSnap) => {
      const data = docSnap.data();
      try {
        console.log(`🔓 Decrypting thought ${docSnap.id}...`);
        console.log(`   Encrypted title: "${data.title}"`);
        console.log(`   Encrypted content: "${data.content}"`);
        console.log(`   Using passkey: "${passkey}" (length: ${passkey.length})`);
        
        // Decrypt title and content (handles empty strings automatically)
        const decryptedTitle = EncryptionService.decrypt(data.title || '', passkey);
        const decryptedContent = EncryptionService.decrypt(data.content || '', passkey);
        
        console.log(`   ✅ Decrypted title: "${decryptedTitle}"`);
        console.log(`   ✅ Decrypted content: "${decryptedContent.substring(0, 50)}${decryptedContent.length > 50 ? '...' : ''}"`);
        
        thoughts.push({
          id: docSnap.id,
          ...data,
          title: decryptedTitle || 'Untitled',
          content: decryptedContent || '',
        } as Thought);
      } catch (error) {
        console.error(`❌ Error decrypting thought ${docSnap.id}:`, error);
        console.error(`   Encrypted title: ${data.title}`);
        console.error(`   Encrypted content: ${data.content?.substring(0, 100)}`);
        console.error(`   Passkey used: "${passkey}"`);
        // If decryption fails, add thought with locked status
        thoughts.push({
          id: docSnap.id,
          ...data,
          title: '🔒 Locked',
          content: 'Unable to decrypt this thought. Invalid passkey.',
        } as Thought);
      }
    });

    return thoughts;
  } catch (error) {
    console.error('Error getting thoughts:', error);
    throw error;
  }
};

// Update a thought
export const updateThought = async (
  thoughtId: string, 
  updates: ThoughtUpdate,
  passkey: string
): Promise<void> => {
  try {
    const thoughtRef = doc(db, THOUGHTS_COLLECTION, thoughtId);
    const updateData: any = {
      updatedAt: serverTimestamp()
    };

    // Encrypt title and content if provided
    if (updates.title !== undefined) {
      updateData.title = EncryptionService.encrypt(updates.title, passkey);
    }
    if (updates.content !== undefined) {
      updateData.content = EncryptionService.encrypt(updates.content, passkey);
    }
    if (updates.labels !== undefined) {
      updateData.labels = updates.labels;
    }

    await updateDoc(thoughtRef, updateData);
  } catch (error) {
    console.error('Error updating thought:', error);
    throw error;
  }
};

// Delete a thought
export const deleteThought = async (thoughtId: string): Promise<void> => {
  try {
    const thoughtRef = doc(db, THOUGHTS_COLLECTION, thoughtId);
    await deleteDoc(thoughtRef);
  } catch (error) {
    console.error('Error deleting thought:', error);
    throw error;
  }
};

// Get all unique labels for a user
export const getUserLabels = async (userId: string): Promise<string[]> => {
  try {
    const q = query(
      collection(db, THOUGHTS_COLLECTION),
      where('userId', '==', userId)
    );

    const querySnapshot = await getDocs(q);
    const labelsSet = new Set<string>();

    querySnapshot.forEach((docSnap) => {
      const data = docSnap.data();
      if (data.labels && Array.isArray(data.labels)) {
        data.labels.forEach((label: string) => labelsSet.add(label));
      }
    });

    return Array.from(labelsSet).sort();
  } catch (error) {
    console.error('Error getting labels:', error);
    throw error;
  }
};

// Re-encrypt all thoughts with new passkey
export const reEncryptThoughts = async (
  userId: string,
  oldPasskey: string,
  newPasskey: string,
  onProgress?: (current: number, total: number) => void
): Promise<{ success: number; failed: number }> => {
  try {
    const thoughts = await getUserThoughts(userId, oldPasskey);
    let success = 0;
    let failed = 0;

    for (let i = 0; i < thoughts.length; i++) {
      const thought = thoughts[i];

      // Check if thought is locked (decryption failed)
      if (thought.title === '🔒 Locked') {
        failed++;
        onProgress?.(i + 1, thoughts.length);
        continue;
      }

      try {
        await updateThought(
          thought.id,
          { title: thought.title, content: thought.content },
          newPasskey
        );
        success++;
      } catch (error) {
        console.error(`Failed to re-encrypt thought ${thought.id}:`, error);
        failed++;
      }

      onProgress?.(i + 1, thoughts.length);
    }

    return { success, failed };
  } catch (error) {
    console.error('Error re-encrypting thoughts:', error);
    throw error;
  }
};

